import { useMemo } from "react";
import useFetchGraphData from "../useFetchGraphData";
import {
  isComparisonPeriod,
  periodLabel,
  toApiQuery,
} from "../../pages/upstreamDownstream/usdsParams";

export const METERS_PER_MILE = 1609.344;
export const toMiles = (meters) =>
  meters == null ? null : meters / METERS_PER_MILE;

/**
 * Reshapes /api/usds/annual rows into one entry per reach for the line view.
 *
 * Every reach comes back, not only the selected ones, because a tributary's
 * lowest site is drawn on the mainstem profile at its confluence; `selected`
 * says which reaches get a panel of their own. A reach whose active sites all
 * carry a stream distance also gets a `profile`, the sites placed by stream
 * mile, which is how Cherry Creek is drawn; the short tributaries have no
 * distances and stay categorical.
 *
 * Exported apart from the hook so it can be tested without react-query.
 *
 * @param {array} data /api/usds/annual rows
 * @param {object} opts {statistic: "median"|"meanval", parameterid, selectedReaches: number[]}
 * @returns {{reaches: array, comparisonRange: object|null}|null}
 */
export const toAnnualSeries = (data, opts) => {
  if (!data?.length) return null;
  const {
    statistic = "median",
    parameterid,
    selectedReaches = [],
  } = opts ?? {};

  const reaches = new Map();

  /* One label per comparison period across every reach, as on the box plots;
     a site with a late-starting record must not become its own series. */
  const comparisonRange = data
    .filter((r) => isComparisonPeriod(r.collect_year))
    .reduce(
      (acc, r) => ({
        min: Math.min(acc.min, r.water_year_min ?? acc.min),
        max: Math.max(acc.max, r.water_year_max ?? acc.max),
      }),
      { min: Infinity, max: -Infinity }
    );
  const hasRange = Number.isFinite(comparisonRange.min);

  data.forEach((row) => {
    if (!reaches.has(row.reach_groupid)) {
      reaches.set(row.reach_groupid, {
        reachGroupid: row.reach_groupid,
        reachLabel: row.reach_label,
        sites: [],
        seenSites: new Set(),
        series: new Map(),
      });
    }
    const reach = reaches.get(row.reach_groupid);

    if (!reach.seenSites.has(row.locationid)) {
      reach.seenSites.add(row.locationid);
      reach.sites.push({
        locationid: row.locationid,
        locationName: row.location_name,
        streamOrder: row.stream_order,
        isSynoptic: row.is_synoptic,
        distanceM: row.distance_m,
        mile: toMiles(row.distance_m),
        distanceEstimated: Boolean(row.distance_estimated),
      });
    }

    const period =
      isComparisonPeriod(row.collect_year) && hasRange
        ? periodLabel(
            row.collect_year,
            comparisonRange.min,
            comparisonRange.max
          )
        : periodLabel(row.collect_year, row.water_year_min, row.water_year_max);
    const key = `${row.parameterid}|${row.collect_year}`;

    if (!reach.series.has(key)) {
      reach.series.set(key, {
        key,
        parameterid: row.parameterid,
        paramAbbrev: row.param_abbrev,
        paramDescrip: row.param_descrip,
        unitName: row.unit_name,
        collectYear: row.collect_year,
        period,
        isComparison: isComparisonPeriod(row.collect_year),
        isSecondary: row.parameterid !== parameterid,
        benchmarkHigh: row.benchmark_high,
        benchmarkLow: row.benchmark_low,
        pointsBySite: new Map(),
      });
    }
    reach.series.get(key).pointsBySite.set(row.location_name, {
      value: row[statistic],
      recordcount: row.recordcount,
      isSynoptic: row.is_synoptic,
    });
  });

  return {
    comparisonRange: hasRange ? comparisonRange : null,
    reaches: Array.from(reaches.values()).map((reach) => {
      reach.sites.sort((a, b) => a.streamOrder - b.streamOrder);
      const labels = reach.sites.map((s) => s.locationName);
      const everySiteHasDistance = reach.sites.every((s) => s.mile != null);
      return {
        reachGroupid: reach.reachGroupid,
        reachLabel: reach.reachLabel,
        selected:
          !selectedReaches.length ||
          selectedReaches.includes(reach.reachGroupid),
        sites: reach.sites,
        labels,
        profile: everySiteHasDistance
          ? {
              available: true,
              minMile: Math.min(...reach.sites.map((s) => s.mile)),
              maxMile: Math.max(...reach.sites.map((s) => s.mile)),
              estimatedSites: reach.sites
                .filter((s) => s.distanceEstimated)
                .map((s) => s.locationName),
            }
          : { available: false },
        /* Sites with no result in a period become gaps rather than zeroes. */
        series: Array.from(reach.series.values()).map((s) => ({
          ...s,
          data: labels.map((name) => s.pointsBySite.get(name)?.value ?? null),
          counts: labels.map(
            (name) => s.pointsBySite.get(name)?.recordcount ?? 0
          ),
        })),
      };
    }),
  };
};

/**
 * The landmarks for one reach, in the shape the chart layers read.
 * @param {array} rows /api/usds/landmarks rows
 * @param {number} reachGroupid
 */
export const landmarksForReach = (rows, reachGroupid) =>
  (rows ?? [])
    .filter((r) => r.reach_groupid === reachGroupid)
    .map((r) => ({
      landmarkId: r.landmark_id,
      label: r.label,
      kind: r.kind,
      afterLocationName: r.after_location_name,
      beforeLocationName: r.before_location_name,
      afterMile: toMiles(r.after_distance_m),
      beforeMile: toMiles(r.before_distance_m),
      mile: toMiles(r.distance_m),
      distanceEstimated: Boolean(r.distance_estimated),
      mouthLocationName: r.mouth_location_name ?? null,
    }));

/**
 * Tributary mouths to draw on a mainstem profile: each confluence landmark
 * with a mouth site, carrying that site's value for every primary series so
 * the profile can place a point at the confluence mile.
 * @param {array} landmarks landmarksForReach output for the mainstem reach
 * @param {array} reaches toAnnualSeries reaches (all of them)
 * @returns {array} [{label, mile, distanceEstimated, mouthLocationName, valuesByKey: Map}]
 */
export const profileMouths = (landmarks, reaches) =>
  (landmarks ?? [])
    .filter(
      (lm) =>
        lm.kind === "confluence" && lm.mile != null && lm.mouthLocationName
    )
    .map((lm) => {
      const home = (reaches ?? []).find((r) =>
        r.sites.some((s) => s.locationName === lm.mouthLocationName)
      );
      const valuesByKey = new Map();
      home?.series.forEach((s) => {
        const pt = s.pointsBySite.get(lm.mouthLocationName);
        if (pt && pt.value != null) valuesByKey.set(s.key, pt);
      });
      return {
        label: lm.label,
        mile: lm.mile,
        distanceEstimated: lm.distanceEstimated,
        mouthLocationName: lm.mouthLocationName,
        valuesByKey,
      };
    });

/**
 * Feeds the line view.
 * @param {object} params parsed URL state
 * @param {number} year the water year to draw, after defaults are resolved
 */
export default function useUsdsAnnualSeries(params, year) {
  const enabled = Number.isInteger(year);

  /* All reaches, not only the selected ones: the profile needs the tributary
     mouths' values. The payload is a few dozen rows either way. */
  const query = enabled
    ? {
        ...toApiQuery(params, year),
        reaches: undefined,
        ...(params.parameter2 ? { parameter2: params.parameter2 } : {}),
      }
    : null;

  const {
    data,
    isLoading, // true only with nothing cached; isFetching would unmount the chart on every refetch
    isError,
  } = useFetchGraphData(query, "usds/annual");

  const statistic = params.statistic === "mean" ? "meanval" : "median";

  return useMemo(() => {
    const series = toAnnualSeries(data, {
      statistic,
      parameterid: params.parameterid,
      selectedReaches: params.reaches ?? [],
    });
    return {
      allReaches: series?.reaches ?? [],
      reaches: (series?.reaches ?? []).filter((r) => r.selected),
      comparisonRange: series?.comparisonRange ?? null,
      isLoading,
      isError,
    };
  }, [data, isLoading, isError, statistic, params.parameterid, params.reaches]);
}
